import {
	getSeedConfigurationById,
	createSeedConfiguration,
	updateSeedConfiguration,
	deleteSeedConfiguration,
	createWorkflowDispatchEvent,
	getWorkflowRunById,
	fetchWorkflowRunLogsById,
	createCompanyLog as createCompanyLogService,
	fetchCompanyLogs as getCompanyLogsService,
} from "../../../Api/services/seeder";

/**
 * Create a seeded company using the provided configuration
 * @param {Object} config - The configuration to use for seeding
 * @returns {Promise<Object>} The API response
 * @throws {Error} If the operation fails
 */
export const createSeededCompany = async (config) => {
	try {
		const stringifiedConfig = typeof config === "string" ? config : JSON.stringify(config);
		const response = await createWorkflowDispatchEvent(stringifiedConfig);
		return response;
	} catch (error) {
		console.error("Failed to create seeded company:", error);
		throw error;
	}
};

/**
 * Get the status of a workflow run by its ID
 * @param {string} runId - The ID of the workflow run
 * @returns {Promise<string>} The status of the workflow run
 */
export const getWorkflowRunStatusById = async (runId) => {
	try {
		if (!runId) {
			throw new Error("Workflow run ID is required");
		}

		const response = await getWorkflowRunById(runId);
		// The GitHub API returns the status directly in the data object
		const workflowRun = response?.data?.data;

		if (!workflowRun || !workflowRun.status) {
			console.error("Invalid workflow run response:", workflowRun);
			throw new Error("Invalid response format: status not found");
		}

		return workflowRun.status;
	} catch (error) {
		console.error("Error getting workflow run status:", error);
		throw error;
	}
};

/**
 * Get the logs of a workflow run by its ID and extract the seeded company ID
 * @param {string} runId - The ID of the workflow run
 * @returns {Promise<string>} The seeded company ID
 */
export const getWorkflowRunLogsById = async (runId) => {
	try {
		if (!runId) {
			throw new Error("Workflow run ID is required");
		}

		const response = await fetchWorkflowRunLogsById(runId);
		const logs = response?.data?.data;

		if (!logs) {
			throw new Error("No logs found in response");
		}

		const seededCompanyLine = logs.split("\n").find((line) => line.includes("Seeded company "));
		if (!seededCompanyLine) {
			throw new Error("Could not find seeded company information in logs");
		}

		const seededCompanyId = seededCompanyLine.split(" ")[3];
		if (!seededCompanyId) {
			throw new Error("Could not extract company ID from logs");
		}

		return seededCompanyId;
	} catch (error) {
		console.error("Error getting workflow run logs:", error);
		throw error;
	}
};

/**
 * Get a seed configuration by its ID
 * @param {string} configurationId - The ID of the configuration to fetch
 * @returns {Promise<Object|null>} The configuration data or null if not found
 */
export const fetchConfigurationById = async (configurationId) => {
	try {
		const response = await getSeedConfigurationById(configurationId);
		const apiData = response.data?.data || response.data || response;

		if (!apiData || !apiData.seedConfiguration) {
			console.warn("No seed configuration found in response:", apiData);
			return null;
		}

		return apiData.seedConfiguration;
	} catch (error) {
		console.error("Error fetching configuration by ID:", error?.response?.data?.error?.message || error.message);
		return null;
	}
};

/**
 * Update an existing seed configuration
 * @param {string} configurationId - The ID of the configuration to update
 * @param {Object} configurationData - The new configuration data
 * @returns {Promise<Object>} The API response
 * @throws {Error} If the update operation fails
 */
export const updateExistingConfiguration = async (configurationId, configurationData) => {
	try {
		const stringifiedConfig =
			typeof configurationData === "string" ? configurationData : JSON.stringify(configurationData);
		const response = await updateSeedConfiguration(configurationId, stringifiedConfig);
		console.log("response inside updateExistingConfiguration!!!!!!!!", response);
		return response;
	} catch (error) {
		console.error("Error updating configuration:", error);
		if (error.response?.status === 404) {
			const errorMessage =
				error.response?.data?.message ||
				`Configuration with ID '${configurationId}' not found. It may have been deleted or doesn't exist.`;
			throw new Error(errorMessage);
		}
		throw error;
	}
};

/**
 * Create a new seed configuration
 * @param {Object} configurationData - The configuration data to create
 * @returns {Promise<Object>} The API response
 * @throws {Error} If the create operation fails
 */
export const createNewConfiguration = async (configurationData) => {
	try {
		const response = await createSeedConfiguration(configurationData);
		return response.data?.data || response.data || response;
	} catch (error) {
		console.error("Error saving configuration:", error?.response?.data?.error?.message || error.message);
		throw error;
	}
};

/**
 * Delete a seed configuration
 * @param {string} configurationId - The ID of the configuration to delete
 * @returns {Promise<void>}
 * @throws {Error} If the delete operation fails
 */
export const deleteExistingConfiguration = async (configurationId) => {
	try {
		await deleteSeedConfiguration(configurationId);
	} catch (error) {
		console.error("Error deleting configuration:", error);
		throw error;
	}
};

export const createCompanyLog = async (companyId, companyName, environment, timestamp, configName) => {
	try {
		const log = {
			company_id: companyId,
			company_name: companyName,
			environment,
			timestamp,
			config_name: configName,
		};
		const response = await createCompanyLogService(log);
		return response;
	} catch (error) {
		console.error("Error creating company log:", error);
		throw error;
	}
};

export const fetchCompanyLogs = async () => {
	try {
		const response = await getCompanyLogsService();
		return response;
	} catch (error) {
		console.error("Error getting company logs:", error);
		throw error;
	}
};
